import { useState, useEffect, useCallback, useRef } from 'react';
import {
    ArrowLeft, Loader2, Check, X, RefreshCw, AlertCircle, AlertTriangle,
    Trash2, Plus, FileText, Image as ImageIcon, Link2, Link2Off, Lock, Pencil,
    ChevronDown, ChevronLeft, ChevronRight, Landmark, CreditCard, Mail, ListTree, Package, History, Clock,
    Sparkles, Banknote, Repeat, SendHorizontal, QrCode,
} from 'lucide-react';
import NabidkaAkci from '../components/layout/NabidkaAkci';
import PruhSPrejezdem from '../components/layout/PruhSPrejezdem';
import ZalozitDodavateleModal from '../components/ZalozitDodavateleModal';
import Dropdown from '../components/receptura/Dropdown';
import ObalyPanel from '../components/doklady/ObalyPanel';
import DokladOsa, { stavKroku } from '../components/doklady/DokladOsa';
import DokladHistoriePanel from '../components/doklady/DokladHistoriePanel';
import DokladZauctovaniPanel, { rozpadSouhrn, rozpadSedi } from '../components/doklady/DokladZauctovaniPanel';
import PokynyAiModal from '../components/doklady/PokynyAiModal';
import InlinePole from '../components/doklady/InlinePole';
import PolozkyMrizka from '../components/doklady/PolozkyMrizka';
import { pocetZavad, stavPolozky, leafKategorie } from '../components/doklady/polozkyGrid';
import OdeslatMailemModal from '../components/doklady/OdeslatMailemModal';
import NovaSkladovaPolozkaModal from '../components/doklady/NovaSkladovaPolozkaModal';
import ModalShell from '../components/ui/ModalShell';
import ZaplatitModal from '../components/doklady/ZaplatitModal';
import QrPlatbaModal from '../components/doklady/QrPlatbaModal';
import { cenikShrnuti } from '../utils/cenik';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../lib/uiTokens';


// Read-only zobrazení hodnoty (pro ne-adminy místo dropdownu)
function RoPole({ label, isDark }) {
    return (
        <div className="px-2 py-1.5 rounded text-xs truncate"
            style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'white', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
            {label}
        </div>
    );
}

const fmt = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');

/** Způsob platby — ikona i popisek. Samotné slovo „Převod" mezi ostatními
 *  pilulkami splyne; ikona se pozná dřív, než se text přečte. */
const ZPUSOB_PLATBY = {
    prevod:   { label: 'Převod',   Icon: Landmark },
    hotovost: { label: 'Hotovost', Icon: Banknote },
    karta:    { label: 'Karta',    Icon: CreditCard },
    inkaso:   { label: 'Inkaso',   Icon: Repeat },
};
const ZPUSOB_PLATBY_LABEL = Object.fromEntries(
    Object.entries(ZPUSOB_PLATBY).map(([k, v]) => [k, v.label]),
);

/**
 * Splatnost jako věta, ne datum — „za 7 dní" se čte rychleji než „2. 10. 2026",
 * a hlavně je z toho hned poznat, jestli hoří. Barvu dostane jen tehdy, když
 * opravdu jde o čas; vzdálená splatnost je neutrální údaj.
 */
/** „1 den", „3 dny", „12 dní" — skloňování, ne vždycky „dní". */
const dnySlovy = (n) => `${n} ${n === 1 ? 'den' : n < 5 ? 'dny' : 'dní'}`;

const splatnostSlovy = (datum, BAD_, WARN_) => {
    if (!datum) return null;
    const dnu = Math.round((new Date(datum).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (dnu < 0) return { zbytek: `${dnySlovy(Math.abs(dnu))} po splatnosti`, barva: BAD_ };
    if (dnu === 0) return { zbytek: 'dnes', barva: BAD_ };
    if (dnu <= 7) return { zbytek: `za ${dnySlovy(dnu)}`, barva: WARN_ };
    return { zbytek: null, barva: 'var(--text-secondary)' };
};

const TYP_POLOZKY = [
    { value: '', label: '— vyberte —' },
    { value: 'surovina', label: 'Surovina' },
    { value: 'obal', label: 'Obal' },
    { value: 'spotreba', label: 'Spotřební zboží' },
    { value: 'phm', label: 'PHM (palivo)' },
    { value: 'energie', label: 'Energie' },
    { value: 'sluzba', label: 'Služba' },
    { value: 'investice', label: 'Investice' },
    { value: 'ostatni', label: 'Ostatní' },
];

const TYP_KATALOG_FK = {
    surovina: 'surovina_id',
    obal: 'obal_id',
    spotreba: 'spotrebni_zbozi_id',
    phm: 'tankovani_id',
};

/**
 * Plocha sekce uvnitř detailu. Sloupec detailu už sklo má (`cardStyle`
 * v `DokladyModul`), takže když si ho sekce namače znovu, sčitají se dvě
 * poloprop. bílé vrstvy **a dva blury** — plocha zmléční a text na ní ztratí
 * kontrast. Sekce proto dostane jen inset plochu bez `backdrop-filter`.
 */
function plochaSekce(isDark) {
    return isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
    } : {
        background: 'var(--surface-elevated)',
        border: '1px solid var(--border-subtle)',
    };
}

export default function FinanceDokladyDetail({ dokladId, user, cardStyle, isDark, onBack, onOpenDoklad, inspector = false, inspectorWidth = 0,
    onZmena,
    // Záložky drží modul (přežijí přepnutí dokladu). `skenNode` přijde jen
    // tehdy, když se třetí sloupec s náhledem nevejde a má se zopakovat tady.
    zalozka: zalozkaProp = null, setZalozka: setZalozkaProp = null, skenNode = null }) {
    const [zalozkaLocal, setZalozkaLocal] = useState('doklad');
    const zalozkaAktivni = zalozkaProp ?? zalozkaLocal;
    const nastavZalozku = setZalozkaProp ?? setZalozkaLocal;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null); // 'schvalit' | 'zamitnout' | 'rescan' | null
    const [mailOpen, setMailOpen] = useState(false);
    const [validace, setValidace] = useState([]);
    const [kategorie, setKategorie] = useState([]);
    const [katalog, setKatalog] = useState({ surovina: [], obal: [], spotreba: [] });
    const [jednotky, setJednotky] = useState([]);
    const [aresModalOpen, setAresModalOpen] = useState(false);
    const [rozpadOtevren, setRozpadOtevren] = useState(false);
    const [pokynyOtevreny, setPokynyOtevreny] = useState(false);
    const [validaceRozbalena, setValidaceRozbalena] = useState(false);
    const [pinOdemceni, setPinOdemceni] = useState(null); // PIN pro editaci schváleného dokladu
    const [cenikVysledek, setCenikVysledek] = useState(null); // souhrn zápisu cen po schválení

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
    const prilohaInputRef = useRef(null);
    // Kotva sekce položek — proklik z pruhu výhrad na ni doroluje. Ref musí být
    // nad předčasnými `return` níž, jinak se v těch renderech nevolá.
    const polozkyRef = useRef(null);
    // Položka, na kterou má proklik z pruhu „co brání schválení" doopravdy skočit.
    // `kdy` je razítko, aby druhý klik na tutéž závadu zabral znovu.
    const [zamerena, setZamerena] = useState(null);
    const [zaplatitOtevreny, setZaplatitOtevreny] = useState(false);
    const [qrOtevreny, setQrOtevreny] = useState(false);

    const load = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        Promise.all([
            fetch(`/api/finance/doklady/${dokladId}/detail`).then(r => {
                if (!r.ok) throw new Error('Nepodařilo se načíst doklad');
                return r.json();
            }),
            kategorie.length === 0
                ? fetch('/api/finance/kategorie').then(r => r.json())
                : Promise.resolve(kategorie),
        ])
            .then(([detail, kat]) => {
                setData(detail);
                setValidace(detail.validace_pro_schvaleni || []);
                if (kategorie.length === 0) {
                    setKategorie(kat.filter(c => c.skupina !== 'trzby'));
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message || 'Chyba při načítání');
                setLoading(false);
            });
        // eslint-disable-next-line
    }, [dokladId]);

    useEffect(() => { load(); }, [load]);

    // Souhrn zápisu cen patří k jednomu schválení — při přepnutí dokladu zmizí.
    useEffect(() => { setCenikVysledek(null); }, [dokladId]);

    // Přenačíst detail A ZÁROVEŇ dát vědět seznamu, že se doklad změnil (schválení,
    // zamítnutí, rescan, uložení hlavičky/položek, spárování dodavatele). Seznam se
    // dorovná hned — nečeká na Reverb broadcast ani na fallback polling (30 s).
    const loadAZmena = useCallback((silent = false) => { load(silent); onZmena?.(); }, [load, onZmena]);

    // Katalog skladu pro napojení položek (suroviny / obaly / spotřební zboží)
    useEffect(() => {
        if (!isAdmin) return;
        const norm = (arr) => (Array.isArray(arr) ? arr : (arr?.data || arr?.polozky || []))
            .map(x => ({ id: x.id, nazev: x.nazev || x.name || `#${x.id}` }));
        Promise.all([
            fetch('/api/recepty/suroviny').then(r => r.ok ? r.json() : []).catch(() => []),
            fetch('/api/recepty/obaly').then(r => r.ok ? r.json() : []).catch(() => []),
            fetch('/api/recepty/spotrebni-zbozi').then(r => r.ok ? r.json() : []).catch(() => []),
        ]).then(([sur, obal, spot]) => {
            setKatalog({ surovina: norm(sur), obal: norm(obal), spotreba: norm(spot) });
        }).catch(() => {});
    }, [isAdmin]);

    const nactiJednotky = useCallback(() => {
        fetch('/api/ciselniky/jednotky')
            .then(r => (r.ok ? r.json() : []))
            .then(d => setJednotky(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, []);
    useEffect(() => { nactiJednotky(); }, [nactiJednotky]);

    // Skutečná šířka hlavičky — `inspectorWidth` na ni nestačí, `DokladyModul` ho
    // posílá natvrdo 760 bez ohledu na to, jak široký panel opravdu je. Osa se
    // vedle akcí vejde až od 760 px; pod tím by pravý sloupec rozmačkal název
    // dodavatele na „Barma…“ a číslo dokladu do sloupce po jednom slovu.
    //
    // Stojí to tady nahoře mezi ostatními hooky schválně: níž už jsou `if (loading)`
    // a `if (!doklad)` early returny, za kterými by se po načtení dokladu změnil
    // počet hooků a React by spadl.
    const [hlavickaSirka, setHlavickaSirka] = useState(0);
    const hlavickaRef = useCallback((el) => {
        if (!el || typeof ResizeObserver === 'undefined') return;
        const zmer = () => setHlavickaSirka(Math.round(el.getBoundingClientRect().width));
        zmer();
        new ResizeObserver(zmer).observe(el);
    }, []);

    const doklad = data?.doklad;
    const polozky = doklad?.polozky || [];
    const prilohy = doklad?.prilohy || [];
    const povolene = data?.povolene_transitiony || [];

    // Zámek dokladu: schválený a dál se dá měnit jen po zadání PINu
    const ZAMCENE_STAVY = ['schvaleny', 'ceka_na_platbu', 'zaplaceny', 'zauctovany', 'zamitnuty'];
    const dokladSchvalen = doklad && ZAMCENE_STAVY.includes(doklad.stav);
    // Uzavřený měsíc zamyká vždy, i s PINem. Odemčení si pamatuje i server
    // (`odemceno`), takže platí pro všechny panely a přežije znovunačtení.
    const mesicUzavren = !!data?.mesic_uzavren;
    const dokladZamceny = mesicUzavren || (dokladSchvalen && !pinOdemceni && !data?.odemceno);
    // Zámek dokladu: zamčený je varovný, odemčený potvrzený — jedna barva pro celý pruh.
    const zamekBarva = dokladZamceny ? WARN : OK;

    const odemknoutPinem = async () => {
        const pin = window.prompt('Zadej svůj PIN pro odemčení úprav schváleného dokladu:');
        if (!pin) return;
        try {
            const r = await fetch('/api/finance/doklady/over-pin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ pin, doklad_id: dokladId }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok && d.ok) setPinOdemceni(pin);
            else alert('Nesprávný PIN.');
        } catch { alert('Chyba ověření PINu.'); }
    };

    const zamknout = async () => {
        setPinOdemceni(null);
        await fetch('/api/finance/doklady/over-pin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ zamknout: true, doklad_id: dokladId }),
        }).catch(() => {});
        load(true);
    };

    const handleSchvalit = async () => {
        // Flush rozdělaného autosave (např. množství v položce ukládané onBlur):
        // odhlášením fokusu se pending PATCH stihne odeslat DŘÍV, než doklad
        // schválením zamkneme — jinak dorazí na zamčený doklad a spadne na 423.
        if (typeof document !== 'undefined') document.activeElement?.blur?.();
        if (!confirm('Schválit doklad? Nákupní ceny položek se zapíší do ceníků k datu dokladu. U hotovostní úhrady doklad přejde rovnou do Zaplaceno.')) return;
        setAction('schvalit');
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}/schvalit`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': token },
            });
            const json = await r.json();
            if (!r.ok) {
                setValidace(json.chyby || [json.error || 'Schválení selhalo']);
            } else {
                setCenikVysledek(json.cenik || null);
                loadAZmena();
            }
        } catch (e) {
            setValidace(['Síťová chyba']);
        }
        setAction(null);
    };

    const handleZamitnout = async () => {
        const duvod = prompt('Důvod zamítnutí (min 3 znaky):');
        if (!duvod || duvod.length < 3) return;
        setAction('zamitnout');
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}/zamitnout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ duvod }),
            });
            if (r.ok) loadAZmena();
            else {
                const json = await r.json();
                alert(json.error || 'Zamítnutí selhalo');
            }
        } catch (e) { alert('Síťová chyba'); }
        setAction(null);
    };

    /**
     * Hotovost se označí zaplacená jedním klikem — není k čemu ji párovat.
     * Převod, karta a inkaso jsou pohyb na účtu, takže se otevře modál,
     * který nabídne nespárované platby a doklad se zavře přes ně.
     */
    const handleZaplatit = async () => {
        if (doklad?.zpusob_platby !== 'hotovost') { setZaplatitOtevreny(true); return; }
        if (!confirm('Označit doklad jako zaplacený hotově?')) return;
        setAction('zaplatit');
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}/zaplatit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ duvod: 'placeno hotově' }),
            });
            if (r.ok) loadAZmena();
            else { const json = await r.json().catch(() => ({})); alert(json.error || 'Označení zaplaceno selhalo'); }
        } catch (e) { alert('Síťová chyba'); }
        setAction(null);
    };

    /**
     * Razítko „příkaz jsem dal do banky". Není to zaplaceno — zaplaceno řekne
     * až spárovaný bankovní pohyb; tohle je poznámka administrátora dokladů
     * o tom, co už v bankovnictví zadal, aby to nezadal podruhé.
     */
    const handlePlatbaZadana = async (zadano) => {
        setAction('platba_zadana');
        try {
            const r = await fetch('/api/finance/doklady/oznacit-platbu-zadanou', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids: [dokladId], zadano }),
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) alert(json.error || 'Označení platby selhalo');
            else if (!json.zmeneno) alert(json.preskoceno?.[0] || 'Doklad se označit nedal.');
            else loadAZmena();
        } catch { alert('Síťová chyba'); }
        setAction(null);
    };

    const handleSmazat = async () => {
        if (!confirm('Opravdu smazat celý doklad? Tuto akci nelze vrátit.')) return;
        setAction('smazat');
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}/smazat`, {
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': token },
            });
            if (r.ok) { onBack?.(); }
            else { const e = await r.json().catch(() => ({})); alert(e.message || 'Smazání selhalo'); setAction(null); }
        } catch { alert('Síťová chyba'); setAction(null); }
    };

    const handlePrilohaUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAction('priloha');
        try {
            const fd = new FormData();
            fd.append('soubor', file);
            const r = await fetch(`/api/finance/doklady/${dokladId}/prilohy`, {
                method: 'POST', headers: { 'X-CSRF-TOKEN': token }, body: fd,
            });
            if (r.ok) load(true);
            else { const err = await r.json().catch(() => ({})); alert(err.message || 'Nahrání přílohy selhalo'); }
        } catch { alert('Síťová chyba'); }
        if (e.target) e.target.value = '';
        setAction(null);
    };

    const handleRescan = async () => {
        if (!confirm('Opětovně naskenovat? Smaže se aktuální AI rozpoznání (ručně potvrzené položky zůstanou).')) return;
        setAction('rescan');
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}/rescan`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': token },
            });
            if (r.ok) loadAZmena();
            else {
                const json = await r.json();
                alert(json.error || 'Rescan selhal');
            }
        } catch (e) { alert('Síťová chyba'); }
        setAction(null);
    };

    const handleNeniDuplikat = async () => {
        setAction('neni_duplikat');
        try {
            await fetch(`/api/finance/doklady/${dokladId}/neni-duplikat`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': token },
            });
            load(true);
        } catch (e) { alert('Síťová chyba'); }
        setAction(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin" style={{ color: SECTION_COLOR }} />
            </div>
        );
    }

    if (error || !doklad) {
        return (
            <div className="text-center py-20">
                <AlertCircle size={32} className="mx-auto mb-2" style={{ color: BAD }} />
                <div className="text-sm mb-4" style={{ color: 'var(--text-primary)' }}>{error || 'Doklad nenalezen'}</div>
                <button onClick={onBack} className="text-sm" style={{ color: SECTION_COLOR }}>← Zpět na Inbox</button>
            </div>
        );
    }

    // V inspektoru rozhoduje o rozvržení šířka panelu, ne viewportu — Tailwind
    // breakpointy (md:/lg:) měří okno, takže na širokém monitoru s úzkým panelem
    // sázely obsah do sloupců, které se dovnitř nevešly.
    const sirka = inspector ? inspectorWidth : Infinity;
    const osaVpravo = hlavickaSirka >= 760;

    const uzky = sirka < 560; // úzký panel / mobil → pole skládej pod sebe

    const hraniceZalozek = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    // Zaplacený doklad nebo zadaná platba už splatnost neřeší — žádné „po splatnosti".
    const splatnostVyresena = !!(doklad.zaplaceno_at || doklad.platba_zadana_at || ['zaplaceny', 'zauctovany', 'zamitnuty'].includes(doklad.stav));
    const splatnost = splatnostVyresena ? null : splatnostSlovy(doklad.datum_splatnosti, BAD, WARN);
    const rozpad = rozpadSouhrn(doklad);
    const rozpadOk = rozpadSedi(doklad);

    /**
     * Uloží JEDNO pole hlavičky. Endpoint má všechna pravidla `sometimes`, takže
     * částečný PATCH je v pořádku — pošle se jen to, co se změnilo. Vrací `true`,
     * aby `InlinePole` vědělo, co zobrazit.
     */
    const ulozHlavicku = async (patch) => {
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify(patch),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                alert(e.message || 'Uložení selhalo');
                return false;
            }
            loadAZmena(true);
            return true;
        } catch {
            alert('Síťová chyba');
            return false;
        }
    };

    /** Odpárovat dodavatele — doklad zůstane bez něj a půjde spárovat znovu. */
    const odparovatDodavatele = async () => {
        if (!confirm('Odpárovat dodavatele od tohoto dokladu? Doklad zůstane bez dodavatele a půjde spárovat znovu.')) return;
        await ulozHlavicku({ dodavatel_id: null });
    };

    /**
     * Proklik z pruhu výhrad na položky. Na širokém rozvržení jsou položky
     * hned pod hlavičkou v témže scrollu, takže stačí dorolovat; na úzkém
     * se přepne záložka.
     */
    /**
     * Proklik z pruhu výhrad. Doskrolovat na *sekci* položek nestačí — pruh
     * slibuje „skoč na místo, kde to spravíš", a to je konkrétní řádek, ne
     * nadpis nad dvaceti dalšími. U dokladu o třech položkách navíc sekce
     * ležela na obrazovce už předtím, takže klik zdánlivě nedělal nic.
     *
     * `kod` je závada z `stavPolozky` (`typ` / `katalog` / `ucet`); najde se
     * první položka, která ji má, otevře se jí editor a řádek se doroluje
     * doprostřed. Bez `kod` (nebo když se položka nenajde) zbyde původní skok
     * na sekci.
     */
    const skocNaPolozky = (kod = null) => {
        if (uzky) nastavZalozku('polozky');
        const cil = kod ? polozky.find(p => stavPolozky(p).chybi.some(c => c.kod === kod)) : null;
        if (cil) { setZamerena({ id: cil.id, kdy: Date.now() }); return; }
        if (!uzky) polozkyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /**
     * Výhrady do JEDNOHO pruhu s proklikem. Server posílá plochý seznam vět —
     * u nenatypovaného dokladu jednu na každou položku, takže z toho na panelu
     * byl odstavec, který se musel sbalovat. Věty se tedy seskupí do příčin
     * („2 položky bez P&L účtu") a každá příčina ví, kam poslat: chybějící
     * dodavatel na ARES, položky na svou sekci. Text daleko od místa, kde se
     * závada spravuje, je nejdražší část celé obrazovky.
     */
    const zavady = pocetZavad(polozky);
    const vyhrady = [
        !doklad.dodavatel_id && {
            id: 'dodavatel', text: 'Dodavatel není spárovaný',
            akce: 'Dohledat v ARES', onClick: () => setAresModalOpen(true),
        },
        zavady.bezTypu > 0 && {
            id: 'typ',
            text: `${zavady.bezTypu} ${zavady.bezTypu === 1 ? 'položka nemá' : zavady.bezTypu < 5 ? 'položky nemají' : 'položek nemá'} určený typ`,
            akce: 'Doplnit typy', onClick: () => skocNaPolozky('typ'),
        },
        zavady.bezKatalogu > 0 && {
            id: 'katalog',
            text: `${zavady.bezKatalogu} ${zavady.bezKatalogu === 1 ? 'položka není' : 'položky nejsou'} v katalogu`,
            akce: 'Spárovat', onClick: () => skocNaPolozky('katalog'),
        },
        zavady.bezUctu > 0 && {
            id: 'ucet',
            text: `${zavady.bezUctu} ${zavady.bezUctu === 1 ? 'položka bez' : zavady.bezUctu < 5 ? 'položky bez' : 'položek bez'} P&L účtu`,
            akce: 'Doplnit účty', onClick: () => skocNaPolozky('ucet'),
        },
    ].filter(Boolean);

    /**
     * Co server hlásí a do příčin se to zařadit nedá — hlavně rozdíl součtu
     * položek proti celkové částce (počítá se i z obalových pohybů, takže na
     * klientu se dopočítat nedá). Věty o dodavateli a položkách už jeden pruh
     * výše mají, takže se tady neopakují; zbytek se ukáže tak, jak přišel.
     */
    const zbyleVyhrady = validace.filter(v =>
        !/^(Položka|Surovina|Obal|Spotřební zboží|Dodavatel)/i.test(String(v).trim()));
    /**
     * Na širokém rozvržení ŽÁDNÉ záložky nejsou: prostřední sloupec je jeden
     * scroll (hlavička → položky → obaly) a co k dokladu jen informuje nese
     * třetí sloupec. Rejstřík nad tím navigoval mezi třemi věcmi, které se
     * vešly na jednu obrazovku.
     *
     * Když se třetí sloupec nevejde (`skenNode`), jeho obsah sem přijde jako
     * záložky vedle práce — záložka je pak „na který sloupec se dívám".
     */
    const zalozky = [
        { id: 'polozky', label: 'Položky', Icon: ListTree, pocet: polozky.length || null },
        ...(skenNode ? [
            { id: 'sken', label: 'Sken', Icon: ImageIcon },
            { id: 'zauctovani', label: 'Zaúčtování', Icon: FileText },
            { id: 'historie', label: 'Historie', Icon: History },
        ] : []),
    ];
    const aktivniZalozka = zalozky.some(z => z.id === zalozkaAktivni) ? zalozkaAktivni : 'polozky';

    /* Akce dokladu na jednom místě — mobilní hlavička z nich vytahuje primární
       na plochu a zbytek dává do nabídky, desktopová je předá `NabidkaAkci`
       celé. Dřív seděly v JSX jedné z nich, takže druhá je mít nemohla. */
    const akceDokladu = [
        povolene.includes('schvaleny') && {
            id: 'schvalit', label: 'Schválit', Icon: Check, barva: OK,
            primarni: true, onClick: handleSchvalit,
        },
        povolene.includes('zamitnuty') && {
            id: 'zamitnout', label: 'Zamítnout', Icon: X, barva: BAD,
            primarni: true, onClick: handleZamitnout,
        },
        doklad.stav !== 'zauctovany' && {
            id: 'smazat', label: 'Smazat', Icon: Trash2, barva: BAD,
            onClick: handleSmazat, skrytNaMobilu: true,
        },
        // Zaplaceno projde i u zamčeného dokladu — dobropisy,
        // opravy a doklady z minulosti.
        !['zaplaceny', 'zauctovany'].includes(doklad.stav) && {
            id: 'zaplatit', label: 'Označit zaplaceno', Icon: CreditCard, barva: OK,
            onClick: handleZaplatit,
        },
        // Jen u bezhotovostního dokladu, který banka ještě nezaplatila —
        // jinde razítko nedává smysl (hotovost se v bance nezadává, vyřízený
        // doklad se nerazítkuje). Prázdný způsob platby bere systém jako
        // převod, stejně jako auto-přechod po schválení.
        // QR pro bankovní appku — kód FL{id} ve zprávě pak platbu ve výpisu
        // sám spáruje. Stejná podmínka jako razítko „zadáno do banky".
        ['schvaleny', 'ceka_na_platbu'].includes(doklad.stav)
            && (!doklad.zpusob_platby || doklad.zpusob_platby === 'prevod') && {
            id: 'qr_platba', label: 'QR platba', Icon: QrCode,
            primarni: !doklad.platba_zadana_at, onClick: () => setQrOtevreny(true),
        },
        ['schvaleny', 'ceka_na_platbu'].includes(doklad.stav)
            && (!doklad.zpusob_platby || doklad.zpusob_platby === 'prevod') && {
            id: 'platba_zadana',
            label: doklad.platba_zadana_at ? 'Zrušit zadání platby' : 'Platbu jsem zadal do banky',
            Icon: SendHorizontal,
            barva: doklad.platba_zadana_at ? undefined : OK,
            onClick: () => handlePlatbaZadana(!doklad.platba_zadana_at),
        },
        prilohy.length > 0 && ['k_doplneni', 'rozpoznan', 'ke_schvaleni'].includes(doklad.stav) && {
            id: 'rescan', label: 'Rescan AI', Icon: RefreshCw, onClick: handleRescan,
        },
        prilohy.length > 0 && {
            id: 'mail', label: 'Odeslat mailem', Icon: Mail, onClick: () => setMailOpen(true),
        },
        doklad.dodavatel_id && {
            id: 'odparovat', label: 'Odpárovat dodavatele', Icon: Link2Off,
            onClick: odparovatDodavatele,
        },
    ].filter(Boolean);

    return (
        <div style={inspector ? { padding: 16 } : undefined}>
            {/* Hlavička dokladu — rozvržení hlavičky suroviny (`MaterialDetailHeader`):
                identita vlevo, akce vpravo nahoře. Dřív tu byl titulek „Doklad
                FA-260142" a dodavatel s částkou schovaní v šedé metařádce — částka
                na faktuře je hlavní číslo a byla nejmenším textem na obrazovce.
                V inspektoru je hlavička přilepená nahoře (bleedne přes padding
                panelu), ať jsou akce po ruce i po odrolování na položky. */}
            <div
                ref={hlavickaRef}
                style={inspector ? {
                    position: 'sticky', top: 0, zIndex: 5,
                    background: 'var(--surface-raised)',
                    margin: '-16px -16px 12px', padding: '16px 16px 12px',
                    borderBottom: '1px solid var(--border-default)',
                } : { marginBottom: 16, position: 'relative' }}
            >
                {uzky ? (
                    <HlavickaMobil
                        doklad={doklad} isAdmin={isAdmin} dokladZamceny={dokladZamceny} isDark={isDark}
                        splatnost={splatnost} rozpad={rozpad} rozpadOk={rozpadOk}
                        akce={akceDokladu} onBack={onBack} onUloz={ulozHlavicku}
                        onZmenitDodavatele={() => setAresModalOpen(true)}
                        onPokynyAi={() => setPokynyOtevreny(true)}
                        onRozpad={() => setRozpadOtevren(true)}
                    />
                ) : (<>
                {/* Na širokém rozvržení seznam nikam nezmizel — z detailu se
                    nevychází vůbec, jen se klepne na jiný doklad (stejně jako
                    u surovin, kde žádný křížek není). */}
                {/* Řádek se zalamuje — v úzkém inspektoru spadne pravý sloupec
                    (akce + osa) pod název místo toho, aby se oba zmačkaly. */}
                <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                        {!inspector && (
                            <button onClick={onBack} className="p-2 rounded-lg flex-shrink-0"
                                style={{ ...cardStyle, color: 'var(--text-secondary)' }} title="Zpět na Inbox">
                                <ArrowLeft size={16} />
                            </button>
                        )}

                        <div className="min-w-0 flex-1">
                            {/* Identita dokladu. Údaje se edituje PŘÍMO TADY — dole
                                v sekci stály podruhé, takže polovina „Hlavičky
                                dokladu" byla kopie tohohle bloku. Tečkované
                                podtržení říká, co jde přepsat; hodnota přitom
                                zůstane textem, ne formulářovým polem. */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold flex-shrink-0"
                                    style={{ background: `${SECTION_COLOR}29`, color: SECTION_COLOR }}>
                                    {doklad.interni_cislo || `#${doklad.id}`}
                                </span>
                                {doklad.dodavatel_id ? (
                                    <h2 className="font-bold min-w-0 truncate" style={{ fontSize: 19, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                                        {doklad.dodavatel?.nazev}
                                    </h2>
                                ) : (
                                    /* Nespárovaný dodavatel je text z dokladu — ten se
                                       přepsat dá. Spárovaný je vazba do katalogu a mění
                                       se výběrem (ARES), ne přepsáním názvu. */
                                    <InlinePole inline typ="text" hodnota={doklad.dodavatel || ''}
                                        zobrazeni={doklad.dodavatel || 'Bez dodavatele'}
                                        placeholder="Bez dodavatele" editovatelne={isAdmin && !dokladZamceny} isDark={isDark}
                                        sirkaInputu={280}
                                        styl={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', color: doklad.dodavatel ? 'var(--text-primary)' : WARN }}
                                        onSave={(v) => ulozHlavicku({ dodavatel: v })} />
                                )}
                                {doklad.dodavatel_id && isAdmin && (
                                    <>
                                        <button onClick={() => setAresModalOpen(true)} title="Vybrat jiného dodavatele / založit přes ARES"
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium flex-shrink-0"
                                            style={{ color: 'var(--text-tertiary)', border: '1px solid var(--border-default)' }}>
                                            <RefreshCw size={9} /> Změnit
                                        </button>
                                        {/* Pokyny pro AI čtení jsou nastavení DODAVATELE, ne údaj
                                            dokladu — proto sedí u jeho jména, ne v panelu zaúčtování.
                                            Ikona svítí, když už nějaké pokyny má. */}
                                        <button onClick={() => setPokynyOtevreny(true)}
                                            aria-label="Pokyny pro AI čtení"
                                            title={(doklad.dodavatel?.scan_pokyny || doklad.dodavatel?.scan_jen_souhrn)
                                                ? 'Pokyny pro AI čtení — nastaveno'
                                                : 'Pokyny pro AI čtení'}
                                            className="inline-flex items-center justify-center rounded flex-shrink-0"
                                            style={{
                                                width: 22, height: 20,
                                                color: (doklad.dodavatel?.scan_pokyny || doklad.dodavatel?.scan_jen_souhrn) ? SECTION_COLOR : 'var(--text-tertiary)',
                                                border: `1px solid ${(doklad.dodavatel?.scan_pokyny || doklad.dodavatel?.scan_jen_souhrn) ? `${SECTION_COLOR}66` : 'var(--border-default)'}`,
                                                background: 'transparent',
                                            }}>
                                            <Sparkles size={11} />
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="text-xs mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: 'var(--text-tertiary)' }}>
                                <span>č. dokladu</span>
                                <InlinePole inline typ="text" hodnota={doklad.cislo_dokladu || ''}
                                    zobrazeni={doklad.cislo_dokladu || 'doplnit'} placeholder="doplnit"
                                    editovatelne={isAdmin && !dokladZamceny} isDark={isDark} sirkaInputu={130}
                                    styl={{ fontSize: 12, color: 'var(--text-secondary)' }}
                                    onSave={(v) => ulozHlavicku({ cislo_dokladu: v })} />
                                <span>· VS</span>
                                <InlinePole inline typ="text" hodnota={doklad.variabilni_symbol || ''}
                                    zobrazeni={doklad.variabilni_symbol || 'doplnit'} placeholder="doplnit"
                                    editovatelne={isAdmin && !dokladZamceny} isDark={isDark} sirkaInputu={110}
                                    styl={{ fontSize: 12, color: 'var(--text-secondary)' }}
                                    onSave={(v) => ulozHlavicku({ variabilni_symbol: v })} />
                                {doklad.dodavatel?.ico && <span>· IČO {doklad.dodavatel.ico}</span>}
                            </div>

                            {/* Částka je hlavní číslo dokladu — dostane velikost. */}
                            <div className="flex items-baseline gap-2.5 flex-wrap" style={{ marginTop: 8 }}>
                                <InlinePole inline typ="number" hodnota={doklad.castka_s_dph ?? ''}
                                    zobrazeni={fmt(doklad.castka_s_dph)} editovatelne={isAdmin && !dokladZamceny}
                                    isDark={isDark} sirkaInputu={160}
                                    styl={{ fontSize: 27, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}
                                    onSave={(v) => ulozHlavicku({ castka_s_dph: v })} />
                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                    {doklad.mena && doklad.mena !== 'CZK' ? doklad.mena : 'Kč'} s DPH
                                </span>
                                {/* Rozpad DPH jako pilulka u částky: je to doslova
                                    „jak se těch 45,80 dělí". Vlastní sekci mít nemusí,
                                    editor se otevře klepnutím. Když součet nesedí
                                    s celkovou částkou, pilulka zčervená — a to je
                                    jediné, co o DPH potřebuješ vidět, dokud je to v pořádku. */}
                                {/* Na telefonu je rozpad DPH kontrola, ne rozhodnutí —
                                    schovává se s ostatními podrobnostmi. Když ale
                                    nesedí se součtem, musí být vidět hned. */}
                                {rozpad.length > 0 && (
                                    <button onClick={() => setRozpadOtevren(true)}
                                        className="inline-flex items-center gap-1.5 rounded-md tabular-nums"
                                        style={{
                                            padding: '3px 8px', fontSize: 11.5, background: 'transparent',
                                            border: `1px solid ${rozpadOk === false ? BAD : 'var(--border-default)'}`,
                                            color: rozpadOk === false ? BAD : 'var(--text-secondary)',
                                        }}
                                        title="Rozpad DPH — klepnutím upravíš">
                                        {/* Jedna sazba se vejde celá; u víc sazeb by se
                                            pilulka roztáhla přes půl hlavičky, takže se
                                            sečtou a počet sazeb se řekne slovem. */}
                                        {rozpad.length === 1 ? (
                                            <span>
                                                <span style={{ color: 'var(--text-tertiary)' }}>{rozpad[0].sazba}</span>
                                                {' '}{fmt(rozpad[0].zaklad)}
                                                <span style={{ color: 'var(--text-tertiary)' }}> + </span>{fmt(rozpad[0].dph)}
                                            </span>
                                        ) : (
                                            <span>
                                                {fmt(rozpad.reduce((a, r) => a + r.zaklad, 0))}
                                                <span style={{ color: 'var(--text-tertiary)' }}> + </span>
                                                {fmt(rozpad.reduce((a, r) => a + r.dph, 0))}
                                                <span style={{ color: 'var(--text-tertiary)' }}> · {rozpad.length} sazby</span>
                                            </span>
                                        )}
                                        {rozpadOk === true && <Check size={11} strokeWidth={2.8} style={{ color: OK }} />}
                                        {rozpadOk === false && <AlertCircle size={11} />}
                                    </button>
                                )}
                            </div>

                            {/* Pilulky pod nadpisem, jako kategorie a jednotka u suroviny.
                                Splatnost barví jen tehdy, když opravdu hoří; datum uvnitř
                                se přepisuje stejně jako všechno ostatní. */}
                            {/* Pilulky pod nadpisem, jako kategorie a jednotka u suroviny.
                                Splatnost barví jen tehdy, když opravdu hoří; datum uvnitř
                                se přepisuje stejně jako všechno ostatní. */}
                            <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 8 }}>
                                <span className="inline-flex items-center gap-1.5 rounded-full"
                                    style={{ padding: '4px 9px', fontSize: 11.5, color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                    Vystaveno
                                    <InlinePole inline typ="date"
                                        hodnota={(doklad.datum_vystaveni || doklad.datum || '').slice(0, 10)}
                                        zobrazeni={fmtDate(doklad.datum_vystaveni || doklad.datum)}
                                        editovatelne={isAdmin && !dokladZamceny} isDark={isDark} sirkaInputu={140}
                                        styl={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)' }}
                                        onSave={(v) => ulozHlavicku({ datum_vystaveni: v })} />
                                </span>

                                <span className="inline-flex items-center gap-1.5 rounded-full"
                                    style={{
                                        padding: '4px 9px', fontSize: 11.5,
                                        background: splatnost?.zbytek ? `${splatnost.barva}24` : undefined,
                                        color: splatnost?.zbytek ? splatnost.barva : 'var(--text-secondary)',
                                        border: `1px solid ${splatnost?.zbytek ? `${splatnost.barva}61` : 'var(--border-default)'}`,
                                    }}>
                                    <Clock size={12} /> Splatnost
                                    <InlinePole inline typ="date" hodnota={(doklad.datum_splatnosti || '').slice(0, 10)}
                                        zobrazeni={doklad.datum_splatnosti ? fmtDate(doklad.datum_splatnosti) : 'doplnit'}
                                        placeholder="doplnit"
                                        editovatelne={isAdmin && !dokladZamceny} isDark={isDark} sirkaInputu={140}
                                        styl={{ fontSize: 11.5, fontWeight: 700, color: 'inherit' }}
                                        onSave={(v) => ulozHlavicku({ datum_splatnosti: v })} />
                                    {splatnost?.zbytek && <span style={{ fontWeight: 600 }}>· {splatnost.zbytek}</span>}
                                </span>

                                <span className="inline-flex items-center gap-1.5 rounded-full"
                                    style={{ padding: '4px 9px', fontSize: 11.5, color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                                    {(() => {
                                        const P = ZPUSOB_PLATBY[doklad.zpusob_platby]?.Icon;
                                        return P ? <P size={12} style={{ flexShrink: 0 }} /> : null;
                                    })()}
                                    <InlinePole inline typ="dropdown" hodnota={doklad.zpusob_platby || ''}
                                        zobrazeni={ZPUSOB_PLATBY_LABEL[doklad.zpusob_platby] || 'způsob platby'}
                                        options={Object.entries(ZPUSOB_PLATBY).map(([v, m]) => ({ value: v, label: m.label }))}
                                        editovatelne={isAdmin && !dokladZamceny} isDark={isDark} sirkaInputu={130}
                                        styl={{ fontSize: 11.5, color: 'var(--text-secondary)' }}
                                        onSave={(v) => ulozHlavicku({ zpusob_platby: v })} />
                                </span>
                                {/* Razítko je mezikrok, ne stav: příkaz leží v bance,
                                    peníze ještě neodešly. Jakmile se pohyb spáruje,
                                    mluví za doklad pilulka „Z banky" — dvě informace
                                    o téže platbě vedle sebe by si konkurovaly. */}
                                {doklad.platba_zadana_at && !doklad.bank_transakce_id && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full"
                                        style={{ padding: '4px 9px', fontSize: 11.5, whiteSpace: 'nowrap', background: `${OK}1a`, color: OK, border: `1px solid ${OK}40` }}
                                        title={'Příkaz zadán do banky ' + fmtDate(doklad.platba_zadana_at)
                                            + (doklad.platbu_zadal ? ' · ' + doklad.platbu_zadal.jmeno + ' ' + doklad.platbu_zadal.prijmeni : '')
                                            + ' — zaplaceno bude až po spárování s výpisem'}>
                                        <SendHorizontal size={12} /> Zadáno do banky {fmtDate(doklad.platba_zadana_at)}
                                    </span>
                                )}
                                {doklad.bank_transakce_id && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full"
                                        style={{ padding: '4px 9px', fontSize: 11.5, background: `${SECTION_COLOR}1a`, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}40` }}
                                        title={doklad.bank_transakce
                                            ? `Zaplaceno z banky ${fmtDate(doklad.bank_transakce.datum)} · ${fmt(doklad.bank_transakce.castka)} Kč`
                                            : 'Napárováno na bankovní pohyb'}>
                                        <Landmark size={12} /> Z banky
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Akce vpravo nahoře — pořadí jako u suroviny: co je vidět, drží
                        `NabidkaAkci` (norma tři ovladače), Smazat na mobilu nejde vůbec
                        (není to editace, je to zničení záznamu a sedělo by pár pixelů
                        od Schválit). */}
                    {/* Osa patří k akcím, ne pod celou hlavičku: stav dokladu
                        a tlačítka, která ho mění, se čtou spolu. Přes celou šířku
                        působila, že tam nepatří — mezi šesti drobnými popisky
                        zůstávaly na širokém okně půlmetrové mezery. */}
                    <div className="flex flex-col items-end" style={{
                        gap: 10, flexShrink: 0,
                        // Sloupec se roztáhne na výšku řádku (tu určuje identita vlevo)
                        // a obsah se rozrazí: akce nahoře, osa dole u spodní hrany
                        // hlavičky. Jinak by osa visela hned pod tlačítky a pod ní
                        // zůstalo prázdno až k čáře.
                        alignSelf: 'stretch', justifyContent: 'space-between',
                    }}>
                        {/* Na povrchu jen Schválit a Zamítnout — oba jsou `primarni`,
                            takže `kolikVidet={0}` znamená „žádné další se nedoplňují“.
                            S dvojkou se u dokladu, který už schválit nejde, protlačily
                            na povrch první dvě zbývající — a první v pořadí je Smazat. */}
                        {isAdmin && (
                            <NabidkaAkci akce={akceDokladu} isDark={isDark} barva={SECTION_COLOR} kolikVidet={0} />
                        )}
                        {osaVpravo && (
                            <DokladOsa stav={doklad.stav} aiConfidence={doklad.ai_confidence_avg}
                                isDark={isDark} maly />
                        )}
                    </div>
                </div>

                {/* Na užším panelu osa nesedí vedle akcí — klízne pod hlavičku,
                    pořád v úsporné podobě. */}
                {!osaVpravo && (
                    <div style={{ marginTop: 12 }}>
                        <DokladOsa stav={doklad.stav} aiConfidence={doklad.ai_confidence_avg}
                            isDark={isDark} maly />
                    </div>
                )}

                </>)}
            </div>

            {/* Možný duplikát — JEDEN řádek, ne krabice. Nebrzdí schválení, takže
                nemá právo na plochu čtyřřádkového banneru; nad seznamem položek
                se dřív kupil s validací a zámkem do tří barevných obdélníků. */}
            {doklad.mozny_duplikat_id && doklad.mozny_duplikat && (() => {
                const orig = doklad.mozny_duplikat;
                const proc = doklad.duplicita_typ === 'vs' ? 'stejný dodavatel a VS'
                    : doklad.duplicita_typ === 'cislo' ? 'stejný dodavatel a číslo dokladu'
                    : 'stejná částka a datum';
                return (
                    <div className="flex items-center gap-2 mb-3 px-2.5 py-2 rounded-lg"
                        style={{ border: `1px solid ${WARN}59`, background: `${WARN}0d` }}
                        title={`Možný duplikát — ${proc}`}>
                        <Link2 size={13} style={{ color: WARN, flexShrink: 0 }} />
                        <span className="text-xs flex-1 min-w-0 truncate" style={{ color: 'var(--text-secondary)' }}>
                            Podobný doklad <b style={{ color: 'var(--text-primary)' }}>{orig.interni_cislo || `#${orig.id}`}</b>
                            {' · '}{fmt(orig.castka_s_dph)} Kč
                            {orig.datum_vystaveni ? ` · ${fmtDate(orig.datum_vystaveni)}` : ''}
                            {' · '}{proc}
                        </span>
                        <button onClick={() => onOpenDoklad?.(orig.id)}
                            className="text-xs font-semibold flex-shrink-0"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>
                            Otevřít
                        </button>
                        {isAdmin && (
                            <button onClick={handleNeniDuplikat} disabled={action !== null}
                                className="text-xs flex-shrink-0"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                                {action === 'neni_duplikat' ? <Loader2 size={12} className="animate-spin" /> : 'Není duplikát'}
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Co schválení udělalo s ceníky (nákupní ceny se zapisují automaticky) */}
            {cenikVysledek && (
                <div className="mb-4 p-3 rounded-lg" style={{ background: `${OK}14`, border: `1px solid ${OK}40` }}>
                    <div className="flex items-start gap-2">
                        <Check size={16} style={{ color: OK, marginTop: 2, flexShrink: 0 }} />
                        <div className="flex-1">
                            <div className="text-sm font-medium" style={{ color: OK }}>
                                Ceník: {cenikShrnuti(cenikVysledek) || 'žádná skladová položka k zapsání'}
                            </div>
                            {cenikVysledek.polozky?.some(p => p.stav === 'preskoceno') && (
                                <ul className="mt-1 text-xs space-y-0.5 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
                                    {cenikVysledek.polozky.filter(p => p.stav === 'preskoceno').map(p => (
                                        <li key={p.polozka_id}>{p.nazev}: {p.duvod}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <button onClick={() => setCenikVysledek(null)} style={{ color: 'var(--text-tertiary)' }}><X size={14} /></button>
                    </div>
                </div>
            )}

            {/* Co brání schválení — JEDEN pruh, kde každá příčina ví, kam poslat.
                Server hlásí plochý seznam vět a u nenatypovaného dokladu jednu na
                každou položku; z toho byl na panelu odstavec, který se musel
                sbalovat („Zobrazit všech 7 výhrad"), a text stál daleko od řádku,
                který je špatný. Věty se proto seskupí do příčin a každá dostane
                proklik na místo, kde se spraví. */}
            {vyhrady.length > 0 && (
                <div className="mb-3 rounded-xl overflow-hidden" style={{ background: `${WARN}17`, border: `1px solid ${WARN}57` }}>
                    <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
                        <AlertCircle size={15} style={{ color: WARN, flexShrink: 0 }} />
                        <span className="text-[13px] font-bold" style={{ color: WARN }}>
                            {vyhrady.length} {vyhrady.length === 1 ? 'věc brání' : vyhrady.length < 5 ? 'věci brání' : 'věcí brání'} schválení
                        </span>
                        <span className="text-[11.5px] hidden sm:inline" style={{ color: 'var(--text-tertiary)' }}>
                            — klepni a skoč na místo, kde to spravíš
                        </span>
                    </div>
                    {vyhrady.map(v => (
                        <button key={v.id} onClick={v.onClick}
                            className="w-full flex items-center gap-2 text-left"
                            style={{
                                padding: '0 12px', minHeight: uzky ? 44 : 36,
                                background: 'none', border: 'none', borderTop: `1px solid ${WARN}33`,
                                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                            }}>
                            <span className="text-[12.5px] flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{v.text}</span>
                            <span className="text-[11.5px] font-semibold flex-shrink-0" style={{ color: SECTION_COLOR }}>{v.akce}</span>
                            <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        </button>
                    ))}
                </div>
            )}

            {/* Výhrady, které se do příčin zařadit nedají (rozdíl součtu, cizí měna
                bez kurzu) — ukážou se tak, jak přišly ze serveru. */}
            {zbyleVyhrady.length > 0 && (
                <div className="mb-3 p-3 rounded-xl flex items-start gap-2" style={{ background: `${WARN}14`, border: `1px solid ${WARN}40` }}>
                    <AlertCircle size={15} style={{ color: WARN, marginTop: 1, flexShrink: 0 }} />
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold mb-1" style={{ color: WARN }}>Doklad zatím nelze schválit</div>
                        <ul className="text-xs space-y-0.5 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
                            {(validaceRozbalena ? zbyleVyhrady : zbyleVyhrady.slice(0, 5)).map((chyba, i) => <li key={i}>{chyba}</li>)}
                        </ul>
                        {zbyleVyhrady.length > 5 && (
                            <button onClick={() => setValidaceRozbalena(v => !v)} className="mt-1 text-xs font-medium" style={{ color: WARN }}>
                                {validaceRozbalena ? 'Sbalit' : `Zobrazit všech ${zbyleVyhrady.length}`}
                            </button>
                        )}
                    </div>
                </div>
            )}
            {(dokladSchvalen || mesicUzavren) && (
                <div className="rounded-2xl p-4 flex items-center justify-between gap-3"
                    style={{ background: `${zamekBarva}14`, border: `1px solid ${zamekBarva}4d` }}>
                    <div className="flex items-center gap-2 text-sm" style={{ color: zamekBarva }}>
                        <Lock size={15} />
                        {mesicUzavren
                            ? `Měsíc ${doklad.mesic}/${doklad.rok} je uzavřený — doklad nelze upravovat`
                            : dokladZamceny ? 'Doklad je schválený a zamčený' : 'Úpravy odemčeny (PIN)'}
                    </div>
                    {isAdmin && !mesicUzavren && (dokladZamceny ? (
                        <button onClick={odemknoutPinem}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                            style={{ background: WARN }}>
                            Odemknout (PIN)
                        </button>
                    ) : (
                        <button onClick={zamknout}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}>
                            Zamknout
                        </button>
                    ))}
                </div>
            )}

            {/* Záložky jen tam, kde se nevejde třetí sloupec — pak přepínají
                „na který sloupec se dívám". Na širokém rozvržení je prostřední
                sloupec jeden scroll a rejstřík nad ním by navigoval mezi věcmi,
                které jsou všechny vidět. */}
            {uzky && zalozky.length > 1 && (
                <PruhSPrejezdem className="flex items-center gap-1 overflow-x-auto"
                    style={{ borderBottom: `1px solid ${hraniceZalozek}`, marginBottom: 12 }}>
                    {zalozky.map(z => (
                        <button key={z.id} onClick={() => nastavZalozku(z.id)}
                            className="flex items-center gap-1.5 text-[12.5px] font-medium whitespace-nowrap"
                            style={{
                                padding: '9px 10px', minHeight: 44,
                                borderBottom: `2px solid ${aktivniZalozka === z.id ? SECTION_COLOR : 'transparent'}`,
                                color: aktivniZalozka === z.id ? SECTION_COLOR : 'var(--text-tertiary)',
                            }}>
                            <z.Icon size={13} /> {z.label}
                            {z.pocet != null && <span style={{ opacity: 0.6 }}>{z.pocet}</span>}
                        </button>
                    ))}
                </PruhSPrejezdem>
            )}

            {/* PRÁCE: položky a obaly. Nadpis na vlásové lince s akcemi vpravo,
                žádný rámeček kolem skupiny — ten nutí sousední bloky do stejné
                výšky a dělá z nich krabice bez vztahu. */}
            {(!uzky || aktivniZalozka === 'polozky') && (
                <div ref={polozkyRef} style={{ scrollMarginTop: 96 }}>
                    <PolozkyPanel
                        doklad={doklad}
                        polozky={polozky}
                        kategorie={kategorie}
                        katalog={katalog}
                        setKatalog={setKatalog}
                        jednotky={jednotky}
                        zamerena={zamerena}
                        cardStyle={plochaSekce(isDark)}
                        isDark={isDark}
                        isAdmin={isAdmin}
                        uzky={uzky}
                        kompaktni={inspectorWidth > 0 && inspectorWidth < 440}
                        dokladZamceny={dokladZamceny}
                        pin={pinOdemceni}
                        onChange={() => loadAZmena(true)}
                        onReloadJednotky={nactiJednotky}
                        token={token}
                    />

                    <div style={{ marginTop: 14 }}>
                        <ObalyPanel
                            dokladId={doklad.id}
                            pohyby={doklad.obalo_pohyby || []}
                            cardStyle={plochaSekce(isDark)}
                            isDark={isDark}
                            isAdmin={isAdmin}
                            dokladZamceny={dokladZamceny}
                            token={token}
                            onChange={() => loadAZmena(true)}
                        />
                    </div>
                </div>
            )}

            {/* Obsah třetího sloupce, když se nevejde vedle. */}
            {skenNode && uzky && aktivniZalozka === 'sken' && skenNode}

            {skenNode && uzky && aktivniZalozka === 'zauctovani' && (
                <div className="rounded-2xl overflow-hidden" style={plochaSekce(isDark)}>
                    <DokladZauctovaniPanel doklad={doklad} isAdmin={isAdmin && !dokladZamceny} isDark={isDark}
                        mutedColor="var(--text-muted)" kompaktni
                        onSave={ulozHlavicku} onZmena={() => loadAZmena(true)} />
                </div>
            )}

            {skenNode && uzky && aktivniZalozka === 'historie' && (
                <div className="rounded-2xl overflow-hidden" style={plochaSekce(isDark)}>
                    <DokladHistoriePanel log={doklad.workflow_log || []} isDark={isDark}
                        mutedColor="var(--text-muted)" kompaktni />
                </div>
            )}

            {/* Editor rozpadu DPH — otevře se z pilulky u částky. */}
            {rozpadOtevren && (
                <ModalShell open onClose={() => setRozpadOtevren(false)} isDark={isDark}
                    nadpis="Rozpad DPH a zaúčtování" ikona={FileText} barva={SECTION_COLOR} sirka={380}>
                    <DokladZauctovaniPanel doklad={doklad} isAdmin={isAdmin && !dokladZamceny} isDark={isDark}
                        mutedColor="var(--text-muted)" kompaktni
                        onSave={ulozHlavicku} onZmena={() => loadAZmena(true)} />
                </ModalShell>
            )}

            {pokynyOtevreny && doklad.dodavatel_id && (
                <PokynyAiModal dodavatel={doklad.dodavatel} isDark={isDark}
                    onClose={() => setPokynyOtevreny(false)} onUlozeno={() => loadAZmena(true)} />
            )}

            {/* ARES modal pro spárování dodavatele */}
            <ZalozitDodavateleModal
                open={aresModalOpen}
                onClose={() => setAresModalOpen(false)}
                onSuccess={() => loadAZmena()}
                dokladId={doklad.id}
                defaultIco={doklad.scan_dodavatel_ico || ''}
                defaultNazev={doklad.dodavatel?.nazev || doklad.dodavatel || ''}
                cardStyle={cardStyle}
                isDark={isDark}
            />

            {mailOpen && (
                <OdeslatMailemModal doklady={[doklad]} cardStyle={cardStyle} onClose={() => setMailOpen(false)} />
            )}

            {qrOtevreny && (
                <QrPlatbaModal doklad={doklad} isDark={isDark}
                    onClose={() => setQrOtevreny(false)}
                    onPlatbaZadana={() => handlePlatbaZadana(true)} />
            )}

            {zaplatitOtevreny && (
                <ZaplatitModal doklad={doklad} isDark={isDark} token={token}
                    onClose={() => setZaplatitOtevreny(false)}
                    onHotovo={() => loadAZmena(true)} />
            )}
        </div>
    );
}

// ─── Panel položek s inline editorem ─────────────────────────────────────────

/**
 * Hlavička dokladu na telefonu.
 *
 * Rozvržení se rozešlo se širokou obrazovkou schválně. Na desktopu je hlavička
 * pás nad tabulkou a místa má dost; na telefonu je detail JEDINÉ, co je vidět,
 * a hlavička z něj brala dvě třetiny výšky. Horší než ta výška byl ale nepořádek:
 * v jednom bloku se potkaly čtyři tvary tlačítek — orámovaná pilulka „Zpět“,
 * zelený blok „Schválit“ uprostřed obrazovky, čtverec „⋯“ s okrajem a další
 * oválek „Podrobnosti“ — a žádné dva nesdílely výšku ani rádius.
 *
 * Teď má hlavička čtyři pásma a jedno pravidlo na každé:
 *
 *  1. **App bar** — návrat, jméno dodavatele, ostatní akce. Ikony bez rámečků,
 *     44 px na prst. Jediné místo, kde se z obrazovky odchází.
 *  2. **Pruh postupu** — šest kroků workflow ve 3 px místo šesti koleček.
 *  3. **Částka jako hrdina** a vedle ní JEDINÝ údaj, který zastaví ruku nad
 *     tlačítkem: splatnost po termínu, nebo rozpad DPH, když nesedí se součtem.
 *     Pod tím jeden popisný řádek: stav, interní číslo, datum, způsob platby.
 *  4. **Jedno tlačítko přes celou šířku.** Zamítnout a spol. jsou v „⋯“ nahoře:
 *     schvaluje se řádově častěji, a dvě stejně velká tlačítka vedle sebe nutí
 *     číst, místo aby se dalo klepnout.
 *
 * Zbytek (číslo dokladu, VS, IČO, rozpad DPH, termíny, dodavatel) je v plachtě
 * pod „Podrobnosti dokladu“ — edituje se tam úplně stejně jako dřív.
 *
 * Zelená tlačítka: `OK` s **tmavým** textem. Bílá na #10b981 má kontrast
 * 2,5 : 1, tedy pod normou; stejná zelená s tmavým textem má 9 : 1.
 */
function HlavickaMobil({
    doklad, isAdmin, dokladZamceny, isDark, splatnost, rozpad, rozpadOk,
    akce = [], onBack, onUloz, onZmenitDodavatele, onPokynyAi, onRozpad,
}) {
    const [podrobnosti, setPodrobnosti] = useState(false);

    const krok = stavKroku(doklad.stav);
    // Přes celou šířku je jen jedno tlačítko: Schválit, jinak první hlavní
    // akce. QR platba je na mobilu vždy v „⋯" a s ní všechno ostatní — i další
    // „hlavní" akce. Dřív se do nabídky braly jen nehlavní akce, takže QR
    // platba vedle Zamítnout na mobilu nebyla vidět nikde.
    const primarni = akce.find(a => a.primarni && a.id === 'schvalit')
        || akce.find(a => a.primarni && a.id !== 'qr_platba');
    const ostatni = akce.filter(a => a !== primarni);
    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';

    /** Ikonové tlačítko app baru — bez rámečku, plný dotykový cíl. */
    const ikona = (Ikona, popis, onClick) => (
        <button type="button" onClick={onClick} aria-label={popis} title={popis}
            className="inline-flex items-center justify-center flex-shrink-0"
            style={{
                width: 44, height: 44, padding: 0, border: 'none', background: 'transparent',
                color: 'var(--text-primary)', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}>
            <Ikona size={22} />
        </button>
    );

    return (
        <>
            {/* 1. app bar */}
            <div className="flex items-center" style={{ gap: 2, height: 48, margin: '0 -6px' }}>
                {ikona(ChevronLeft, 'Zpět na seznam dokladů', onBack)}
                <span className="flex-1 min-w-0 truncate font-semibold"
                    style={{ fontSize: 16.5, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>
                    {doklad.dodavatel?.nazev || doklad.dodavatel || 'Bez dodavatele'}
                </span>
                {isAdmin && ostatni.length > 0 && (
                    <NabidkaAkci akce={ostatni} jenNabidka isMobile isDark={isDark} barva={SECTION_COLOR} />
                )}
            </div>

            {/* 2. pruh postupu */}
            <DokladOsa stav={doklad.stav} pruh isDark={isDark} />

            {/* 3. částka + to jediné, co může zastavit ruku */}
            <div className="flex items-end justify-between" style={{ gap: 12, marginTop: 14 }}>
                <span className="flex items-baseline min-w-0" style={{ gap: 6 }}>
                    <InlinePole inline typ="number" hodnota={doklad.castka_s_dph ?? ''}
                        zobrazeni={fmt(doklad.castka_s_dph)} editovatelne={isAdmin && !dokladZamceny}
                        isDark={isDark} sirkaInputu={150}
                        styl={{ fontSize: 34, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}
                        onSave={(v) => onUloz({ castka_s_dph: v })} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {doklad.mena && doklad.mena !== 'CZK' ? doklad.mena : 'Kč'}
                    </span>
                </span>

                {splatnost?.zbytek && (
                    <span className="inline-flex items-center flex-shrink-0 font-bold"
                        style={{
                            gap: 5, padding: '5px 10px', borderRadius: 8, fontSize: 12, whiteSpace: 'nowrap',
                            background: `${splatnost.barva}24`, color: splatnost.barva,
                        }}>
                        <Clock size={13} /> {splatnost.zbytek}
                    </span>
                )}
                {!splatnost?.zbytek && rozpadOk === false && (
                    <button type="button" onClick={onRozpad}
                        className="inline-flex items-center flex-shrink-0 font-bold"
                        style={{
                            gap: 5, padding: '5px 10px', borderRadius: 8, fontSize: 12, whiteSpace: 'nowrap',
                            background: `${BAD}24`, color: BAD, border: 'none', cursor: 'pointer',
                        }}>
                        <AlertCircle size={13} /> DPH nesedí
                    </button>
                )}
            </div>

            {/* popisný řádek — stav, identita, datum, platba na jedné řádce */}
            <div className="flex items-center flex-wrap"
                style={{ gap: 6, marginTop: 8, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                {krok && (
                    <span className="inline-flex items-center font-semibold"
                        style={{ gap: 6, color: krok.zamitnut ? BAD : SECTION_COLOR }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: krok.zamitnut ? BAD : SECTION_COLOR }} />
                        {krok.label}
                    </span>
                )}
                <span>·</span>
                <span>{doklad.interni_cislo || `#${doklad.id}`}</span>
                {(doklad.datum_vystaveni || doklad.datum) && (
                    <>
                        <span>·</span>
                        <span>{fmtDate(doklad.datum_vystaveni || doklad.datum)}</span>
                    </>
                )}
                {ZPUSOB_PLATBY_LABEL[doklad.zpusob_platby] && (
                    <>
                        <span>·</span>
                        <span>{ZPUSOB_PLATBY_LABEL[doklad.zpusob_platby]}</span>
                    </>
                )}
                {/* Razítko banky na telefonu stačí slovem — je to odpověď na
                    „zadal jsem to už?", ne údaj, se kterým se dál pracuje. */}
                {doklad.platba_zadana_at && !doklad.bank_transakce_id && (
                    <>
                        <span>·</span>
                        <span className="inline-flex items-center font-semibold" style={{ gap: 4, color: OK }}>
                            <SendHorizontal size={12} /> zadáno {fmtDate(doklad.platba_zadana_at)}
                        </span>
                    </>
                )}
            </div>

            {/* 4. jedno tlačítko, přes celou šířku, v palcové zóně */}
            {isAdmin && primarni && (
                <button type="button" onClick={primarni.onClick}
                    className="inline-flex items-center justify-center w-full font-bold"
                    style={{
                        gap: 9, height: 52, marginTop: 14, borderRadius: 14, border: 'none',
                        background: primarni.barva || OK, color: '#04231a',
                        fontSize: 16.5, letterSpacing: '-0.01em', cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                    }}>
                    <primarni.Icon size={19} strokeWidth={3} /> {primarni.label}
                </button>
            )}

            {/* řádek podrobností — přes celou šířku, ne další oválek */}
            <button type="button" onClick={() => setPodrobnosti(true)}
                className="flex items-center justify-between w-full"
                style={{
                    height: 50, marginTop: 14, padding: '0 2px', background: 'transparent',
                    border: 'none', borderTop: `1px solid ${hranice}`, borderBottom: `1px solid ${hranice}`,
                    color: 'var(--text-primary)', fontSize: 14.5, cursor: 'pointer', textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent',
                }}>
                <span>Podrobnosti dokladu</span>
                <span className="inline-flex items-center" style={{ gap: 8, color: 'var(--text-tertiary)', fontSize: 12.5 }}>
                    VS, IČO, DPH
                    <ChevronRight size={18} />
                </span>
            </button>

            {podrobnosti && (
                <PodrobnostiSheet
                    doklad={doklad} isAdmin={isAdmin} dokladZamceny={dokladZamceny} isDark={isDark}
                    splatnost={splatnost} rozpad={rozpad} rozpadOk={rozpadOk}
                    onClose={() => setPodrobnosti(false)} onUloz={onUloz}
                    onZmenitDodavatele={onZmenitDodavatele} onPokynyAi={onPokynyAi} onRozpad={onRozpad}
                />
            )}
        </>
    );
}

/**
 * Podrobnosti dokladu v plachtě zdola.
 *
 * Údaje, které se na telefonu nečtou při rozhodování, ale musí jít najít
 * a přepsat. Seskupené jako seznam v Nastavení: popisek vlevo, hodnota
 * vpravo, šipka tam, kde se dá klepnout. Proti dřívějšímu rozbalení přímo
 * v hlavičce to nehýbe obsahem pod sebou a nemusí to dávat každé hodnotě
 * tečkované podtržení, aby se poznalo, že je editovatelná.
 */
function PodrobnostiSheet({
    doklad, isAdmin, dokladZamceny, isDark, splatnost, rozpad, rozpadOk,
    onClose, onUloz, onZmenitDodavatele, onPokynyAi, onRozpad,
}) {
    const lzeEditovat = isAdmin && !dokladZamceny;
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const skupina = (nadpis, radky) => (
        <div style={{ marginTop: 18 }}>
            <div className="font-bold" style={{ padding: '0 2px 7px', fontSize: 11.5, letterSpacing: '0.07em', color: 'var(--text-tertiary)' }}>
                {nadpis}
            </div>
            <div style={{ borderRadius: 14, border: `1px solid ${hranice}`, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                {radky.filter(Boolean)}
            </div>
        </div>
    );

    const radek = (klic, popisek, hodnota, onClick = null) => {
        const Prvek = onClick ? 'button' : 'div';
        return (
            <Prvek key={klic} {...(onClick ? { type: 'button', onClick } : null)}
                className="flex items-center justify-between w-full"
                style={{
                    gap: 12, minHeight: 48, padding: '11px 14px', textAlign: 'left',
                    background: 'transparent', border: 'none', borderTop: `1px solid ${hranice}`,
                    cursor: onClick ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent',
                }}>
                <span style={{ fontSize: 13.5, color: 'var(--text-secondary)', flexShrink: 0 }}>{popisek}</span>
                <span className="inline-flex items-center min-w-0" style={{ gap: 8, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {hodnota}
                    {onClick && <ChevronRight size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                </span>
            </Prvek>
        );
    };

    return (
        <ModalShell open onClose={onClose} isDark={isDark} sirka={520}
            nadpis="Podrobnosti dokladu" ikona={FileText} barva={SECTION_COLOR}>
            <div style={{ padding: '4px 16px 20px' }}>

                {skupina('DOKLAD', [
                    radek('cislo', 'Číslo dokladu', (
                        <InlinePole inline typ="text" hodnota={doklad.cislo_dokladu || ''}
                            zobrazeni={doklad.cislo_dokladu || 'doplnit'} placeholder="doplnit"
                            editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={180}
                            styl={{ fontSize: 14, fontWeight: 600, color: doklad.cislo_dokladu ? 'var(--text-primary)' : WARN }}
                            onSave={(v) => onUloz({ cislo_dokladu: v })} />
                    )),
                    radek('vs', 'Variabilní symbol', (
                        <InlinePole inline typ="text" hodnota={doklad.variabilni_symbol || ''}
                            zobrazeni={doklad.variabilni_symbol || 'doplnit'} placeholder="doplnit"
                            editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={150}
                            styl={{ fontSize: 14, fontWeight: 600, color: doklad.variabilni_symbol ? 'var(--text-primary)' : WARN }}
                            onSave={(v) => onUloz({ variabilni_symbol: v })} />
                    )),
                    radek('interni', 'Interní číslo', doklad.interni_cislo || `#${doklad.id}`),
                ])}

                {skupina('PENÍZE', [
                    rozpad.length > 0 && radek('dph', 'Rozpad DPH', (
                        <span className="inline-flex items-center tabular-nums" style={{ gap: 6, color: rozpadOk === false ? BAD : 'var(--text-primary)' }}>
                            {rozpad.length === 1
                                ? `${rozpad[0].sazba} · ${fmt(rozpad[0].zaklad)} + ${fmt(rozpad[0].dph)}`
                                : `${fmt(rozpad.reduce((a, r) => a + r.zaklad, 0))} + ${fmt(rozpad.reduce((a, r) => a + r.dph, 0))} · ${rozpad.length} sazby`}
                            {rozpadOk === true && <Check size={14} strokeWidth={2.8} style={{ color: OK }} />}
                            {rozpadOk === false && <AlertCircle size={14} />}
                        </span>
                    ), onRozpad),
                    radek('platba', 'Způsob platby', (
                        <InlinePole inline typ="dropdown" hodnota={doklad.zpusob_platby || ''}
                            zobrazeni={ZPUSOB_PLATBY_LABEL[doklad.zpusob_platby] || 'doplnit'}
                            options={Object.entries(ZPUSOB_PLATBY).map(([v, m]) => ({ value: v, label: m.label }))}
                            editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={150}
                            styl={{ fontSize: 14, fontWeight: 600, color: doklad.zpusob_platby ? 'var(--text-primary)' : WARN }}
                            onSave={(v) => onUloz({ zpusob_platby: v })} />
                    )),
                    doklad.bank_transakce_id && radek('banka', 'Zaplaceno z banky', (
                        <span className="inline-flex items-center" style={{ gap: 6, color: SECTION_COLOR }}>
                            <Landmark size={14} />
                            {doklad.bank_transakce ? fmtDate(doklad.bank_transakce.datum) : 'napárováno'}
                        </span>
                    )),
                ])}

                {skupina('TERMÍNY', [
                    radek('vystaveno', 'Vystaveno', (
                        <InlinePole inline typ="date"
                            hodnota={(doklad.datum_vystaveni || doklad.datum || '').slice(0, 10)}
                            zobrazeni={fmtDate(doklad.datum_vystaveni || doklad.datum)}
                            editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={150}
                            styl={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}
                            onSave={(v) => onUloz({ datum_vystaveni: v })} />
                    )),
                    radek('splatnost', 'Splatnost', (
                        <span className="inline-flex items-center" style={{ gap: 6, color: splatnost?.zbytek ? splatnost.barva : 'var(--text-primary)' }}>
                            <InlinePole inline typ="date" hodnota={(doklad.datum_splatnosti || '').slice(0, 10)}
                                zobrazeni={doklad.datum_splatnosti ? fmtDate(doklad.datum_splatnosti) : 'doplnit'}
                                placeholder="doplnit" editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={150}
                                styl={{ fontSize: 14, fontWeight: 700, color: 'inherit' }}
                                onSave={(v) => onUloz({ datum_splatnosti: v })} />
                            {splatnost?.zbytek && <span style={{ fontWeight: 600 }}>· {splatnost.zbytek}</span>}
                        </span>
                    )),
                ])}

                {skupina('DODAVATEL', [
                    radek('dodavatel', 'Název', (
                        doklad.dodavatel_id ? (
                            <span className="truncate">{doklad.dodavatel?.nazev}</span>
                        ) : (
                            <InlinePole inline typ="text" hodnota={doklad.dodavatel || ''}
                                zobrazeni={doklad.dodavatel || 'Bez dodavatele'} placeholder="Bez dodavatele"
                                editovatelne={lzeEditovat} isDark={isDark} sirkaInputu={180}
                                styl={{ fontSize: 14, fontWeight: 600, color: doklad.dodavatel ? 'var(--text-primary)' : WARN }}
                                onSave={(v) => onUloz({ dodavatel: v })} />
                        )
                    ), doklad.dodavatel_id && isAdmin ? onZmenitDodavatele : null),
                    doklad.dodavatel?.ico && radek('ico', 'IČO', (
                        <span className="tabular-nums">{doklad.dodavatel.ico}</span>
                    )),
                    doklad.dodavatel_id && isAdmin && radek('ai', 'Pokyny pro AI čtení',
                        (doklad.dodavatel?.scan_pokyny || doklad.dodavatel?.scan_jen_souhrn)
                            ? <span style={{ color: SECTION_COLOR }}>{doklad.dodavatel?.scan_jen_souhrn ? (doklad.dodavatel?.scan_pokyny ? 'nastaveno · jen souhrn' : 'jen souhrn') : 'nastaveno'}</span>
                            : <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>žádné</span>,
                        onPokynyAi),
                ])}

            </div>
        </ModalShell>
    );
}

function PolozkyPanel({ doklad, polozky, kategorie, katalog, setKatalog, jednotky = [], zamerena = null, cardStyle, isDark, isAdmin, uzky = false, kompaktni = false, onChange, onReloadJednotky, token, dokladZamceny = false, pin = null }) {
    const dokladId = doklad.id;
    const [novaModal, setNovaModal] = useState(null); // { typ, polozka } | null

    const vytvoritJednotku = async () => {
        const nazev = window.prompt('Název nové jednotky (např. Balení):');
        if (!nazev || !nazev.trim()) return null;
        const zkratka = window.prompt('Zkratka (např. bal, max 10 znaků):');
        if (!zkratka || !zkratka.trim()) return null;
        const r = await fetch('/api/ciselniky/jednotky', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ nazev: nazev.trim(), zkratka: zkratka.trim() }),
        });
        if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.message || 'Vytvoření jednotky selhalo'); return null; }
        const j = await r.json();
        onReloadJednotky?.();
        return j.id;
    };

    const updatePolozka = async (polozka, fields) => {
        const r = await fetch(`/api/finance/doklady/${dokladId}/polozky/${polozka.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify(pin ? { ...fields, pin } : fields),
        });
        if (r.ok) { onChange(); return; }
        // 423 = doklad se mezitím zamkl (souběžné schválení / vypršelý PIN).
        // Není to chyba uživatele — jen obnovíme detail do aktuálního (zamčeného)
        // stavu a dáme vědět, že tahle úprava se neuložila.
        if (r.status === 423) {
            onChange();
            alert('Doklad byl mezitím schválen a uzamčen — tato úprava položky se neuložila. Pro další úpravy doklad odemkni PINem.');
            return;
        }
        const err = await r.json().catch(() => ({}));
        alert(err.message || 'Update položky selhal');
    };

    const deletePolozka = async (polozka) => {
        if (!confirm(`Smazat položku "${polozka.nazev_z_dokladu}"?`)) return;
        const qs = pin ? `?pin=${encodeURIComponent(pin)}` : '';
        const r = await fetch(`/api/finance/doklady/${dokladId}/polozky/${polozka.id}${qs}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': token },
        });
        if (r.ok) onChange();
        else { const e = await r.json().catch(() => ({})); alert(e.message || 'Smazání položky selhalo'); }
    };

    const aktualizovatCenu = async (polozka) => {
        const r = await fetch(`/api/finance/doklady/${dokladId}/polozky/${polozka.id}/aktualizovat-cenu`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify(pin ? { pin } : {}),
        });
        if (r.ok) onChange();
        else { const e = await r.json().catch(() => ({})); alert(e.message || 'Aktualizace ceny selhala'); }
    };

    const [pridavam, setPridavam] = useState(false);
    const [nova, setNova] = useState({ nazev: '', mnozstvi: '1', cena_j: '', dph_sazba: 21 });
    const [ukladamPolozku, setUkladamPolozku] = useState(false);

    const pridatPolozku = async () => {
        const nazev = nova.nazev.trim();
        const mnozstvi = parseFloat(nova.mnozstvi) || 0;
        const cenaJ = parseFloat(nova.cena_j) || 0;
        const sazba = Number(nova.dph_sazba);
        if (!nazev) { alert('Zadej název položky.'); return; }
        const bezDph = Math.round(mnozstvi * cenaJ * 100) / 100;
        const sDph = Math.round(bezDph * (1 + sazba / 100) * 100) / 100;
        setUkladamPolozku(true);
        const r = await fetch(`/api/finance/doklady/${dokladId}/polozky`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({
                nazev_z_dokladu: nazev,
                mnozstvi,
                cena_za_jednotku: cenaJ,
                cena_celkem_bez_dph: bezDph,
                dph_sazba: sazba,
                cena_celkem_s_dph: sDph,
                ...(pin ? { pin } : {}),
            }),
        });
        setUkladamPolozku(false);
        if (r.ok) { setNova({ nazev: '', mnozstvi: '1', cena_j: '', dph_sazba: 21 }); setPridavam(false); onChange(); }
        else { const e = await r.json().catch(() => ({})); alert(e.message || 'Přidání položky selhalo'); }
    };

    const editovatelne = isAdmin && !dokladZamceny;
    const inpP = 'rounded-lg px-2 py-1.5 text-sm';
    const inpPStyle = { ...cardStyle, color: 'var(--text-primary)' };

    // ── Výběr řádků a hromadné zařazení ──────────────────────────────────────
    // Nejčastější práce s dokladem: osm položek od téhož dodavatele, všechny
    // surovina, všechny stejný P&L účet. Po řádcích to bylo šestnáct kliknutí do
    // dropdownů — proto výběr a jeden průchod, stejně jako výběr surovin
    // a přetažení do kategorie ve Spižírně.
    const [vybrane, setVybrane] = useState(() => new Set());
    const [hromadneBezi, setHromadneBezi] = useState(false);
    const [otevrenaId, setOtevrenaId] = useState(null);

    // Proklik z pruhu výhrad: otevři tu položku a doroluj na ni. Čeká se
    // rámeček, až se editor vykreslí — jinak by se skrolovalo na řádek
    // ještě v zavřené výšce a cíl by utekl.
    useEffect(() => {
        if (!zamerena?.id) return;
        setOtevrenaId(zamerena.id);
        const t = setTimeout(() => {
            document.querySelector(`[data-polozka="${zamerena.id}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
        return () => clearTimeout(t);
    }, [zamerena]);

    // Výběr se čistí, jen když se změní sada řádků — ne při každém dorovnání
    // detailu, jinak by ho autosave mazal uprostřed vybírání.
    const idPolozek = polozky.map(p => p.id).join(',');
    useEffect(() => { setVybrane(new Set()); }, [idPolozek]);

    const prepnoutVyber = (id) => setVybrane(prev => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });
    const prepnoutVsechny = () => setVybrane(
        polozky.every(p => vybrane.has(p.id)) ? new Set() : new Set(polozky.map(p => p.id))
    );

    const hromadneZaradit = async (fields) => {
        const ids = [...vybrane];
        if (ids.length === 0) return;
        setHromadneBezi(true);
        const chyby = [];
        for (const id of ids) {
            const p = polozky.find(x => x.id === id);
            if (!p) continue;
            // Potvrdit se dá jen položka, která po zařazení opravdu nic nepostrádá —
            // jinak by zelený řádek lhal.
            const po = { ...p, ...fields };
            const hotova = stavPolozky(po).stav === null;
            try {
                const r = await fetch(`/api/finance/doklady/${dokladId}/polozky/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                    body: JSON.stringify({ ...fields, ...(hotova ? { je_potvrzeno: true } : {}), ...(pin ? { pin } : {}) }),
                });
                if (!r.ok) chyby.push(p.nazev_z_dokladu);
            } catch { chyby.push(p.nazev_z_dokladu); }
        }
        setHromadneBezi(false);
        setVybrane(new Set());
        onChange();
        if (chyby.length) alert(`Nepodařilo se zařadit: ${chyby.join(', ')}`);
    };

    const smazatVybrane = async () => {
        const ids = [...vybrane];
        if (ids.length === 0) return;
        if (!confirm(`Smazat ${ids.length} ${ids.length === 1 ? 'položku' : ids.length < 5 ? 'položky' : 'položek'}?`)) return;
        setHromadneBezi(true);
        const qs = pin ? `?pin=${encodeURIComponent(pin)}` : '';
        for (const id of ids) {
            try {
                await fetch(`/api/finance/doklady/${dokladId}/polozky/${id}${qs}`, {
                    method: 'DELETE', headers: { 'X-CSRF-TOKEN': token },
                });
            } catch { /* co selže, zůstane — dorovnání to ukáže */ }
        }
        setHromadneBezi(false);
        setVybrane(new Set());
        onChange();
    };

    const pocetNeuplnych = polozky.filter(p => stavPolozky(p).stav !== null).length;
    const sumPolozek = polozky.reduce((s, p) => s + parseFloat(p.cena_celkem_s_dph || 0), 0);
    const rozdil = parseFloat(doklad.castka_s_dph || 0) - parseFloat(doklad.zaokrouhleni || 0) - sumPolozek;
    const otevrenaPolozka = polozky.find(p => p.id === otevrenaId) || null;

    /** Plná editace jednoho řádku — tatáž komponenta pro rozbalený řádek i plátno. */
    const editor = (p) => (
        <PolozkaRow
            key={p.id}
            polozka={p}
            kategorie={kategorie}
            katalog={katalog}
            jednotky={jednotky}
            onAktualizovatCenu={() => aktualizovatCenu(p)}
            onVytvoritJednotku={vytvoritJednotku}
            isDark={isDark}
            isAdmin={isAdmin}
            uzky={uzky}
            bezNazvu
            dokladZamceny={dokladZamceny}
            onUpdate={fields => updatePolozka(p, fields)}
            onDelete={() => { setOtevrenaId(null); deletePolozka(p); }}
            onVytvoritSkladovou={() => setNovaModal({ typ: p.typ_polozky, polozka: p })}
        />
    );

    return (
        <div className="rounded-2xl p-4" style={cardStyle}>
            {/* Akce vpravo nahoře — stejné místo i pořadí jako v hlavičce suroviny
                (⋯ vlevo, mazání červeným textem, primární akce vpravo). Dřív tu
                bylo jen „Přidat položku" a mazání se dělalo košíkem na každém
                řádku, takže smazat pět řádků znamenalo pět potvrzení. */}
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide font-semibold" style={{ color: 'var(--text-muted)' }}>
                        Položky ({polozky.length})
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        {polozky.filter(p => p.je_potvrzeno).length} potvrzených
                        {pocetNeuplnych > 0 && (
                            <span style={{ color: WARN }}> · {pocetNeuplnych} {pocetNeuplnych === 1 ? 'čeká' : 'čekají'} na zařazení</span>
                        )}
                        {Math.abs(rozdil) > 1 && (
                            <span style={{ color: BAD }}> · rozdíl {fmt(Math.abs(rozdil))} Kč proti dokladu</span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {editovatelne && vybrane.size > 0 && (
                        <button onClick={smazatVybrane} disabled={hromadneBezi}
                            className="inline-flex items-center gap-1.5 rounded-lg text-xs font-medium"
                            style={{
                                minHeight: uzky ? 40 : 32, padding: '0 10px',
                                color: BAD, border: '1px solid var(--border-default)',
                                background: 'transparent', opacity: hromadneBezi ? 0.5 : 1,
                            }}>
                            <Trash2 size={13} /> Smazat {uzky ? '' : 'vybrané'}
                        </button>
                    )}
                    {editovatelne && !pridavam && (
                        <button onClick={() => setPridavam(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold text-white"
                            style={{ minHeight: uzky ? 40 : 32, padding: '0 12px', background: SECTION_COLOR, border: 'none' }}>
                            <Plus size={14} /> Přidat{uzky ? '' : ' položku'}
                        </button>
                    )}
                </div>
            </div>

            {/* Formulář nové položky (ruční zadání) */}
            {pridavam && (
                <div className="mb-3 p-2.5 rounded-lg space-y-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: '1px solid var(--border-default)' }}>
                    <input className={`${inpP} w-full`} style={inpPStyle} placeholder="Název položky" autoFocus
                        value={nova.nazev} onChange={e => setNova(s => ({ ...s, nazev: e.target.value }))} />
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Množství</div>
                            <input type="number" step="0.001" className={`${inpP} w-full`} style={inpPStyle}
                                value={nova.mnozstvi} onChange={e => setNova(s => ({ ...s, mnozstvi: e.target.value }))} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Cena/j bez DPH</div>
                            <input type="number" step="0.01" className={`${inpP} w-full`} style={inpPStyle} placeholder="0,00"
                                value={nova.cena_j} onChange={e => setNova(s => ({ ...s, cena_j: e.target.value }))} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase mb-0.5" style={{ color: 'var(--text-tertiary)' }}>DPH</div>
                            <Dropdown value={nova.dph_sazba} onChange={v => setNova(s => ({ ...s, dph_sazba: Number(v) }))}
                                options={[{ value: 0, label: '0 %' }, { value: 12, label: '12 %' }, { value: 21, label: '21 %' }]}
                                isDark={isDark} accent={SECTION_COLOR} height={34} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                            Celkem s DPH: <b style={{ color: 'var(--text-secondary)' }}>{fmt(Math.round((parseFloat(nova.mnozstvi) || 0) * (parseFloat(nova.cena_j) || 0) * (1 + Number(nova.dph_sazba) / 100) * 100) / 100)} Kč</b>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={pridatPolozku} disabled={ukladamPolozku}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white"
                                style={{ background: SECTION_COLOR, opacity: ukladamPolozku ? 0.5 : 1 }}>
                                {ukladamPolozku ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Přidat
                            </button>
                            <button onClick={() => setPridavam(false)} className="px-2.5 py-1 rounded-lg text-xs" style={{ color: 'var(--text-secondary)' }}>Zrušit</button>
                        </div>
                    </div>
                </div>
            )}

            {polozky.length === 0 ? (
                <div className="text-center py-6 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    {doklad.ai_scan_id
                        ? 'Doklad nemá žádné položky.'
                        : 'Zatím žádné položky — přidej je ručně tlačítkem „Přidat položku".'}
                </div>
            ) : (
                <>
                    <PolozkyMrizka
                        polozky={polozky}
                        doklad={doklad}
                        kategorie={kategorie}
                        isDark={isDark}
                        siroky={!uzky}
                        kompaktni={kompaktni}
                        editovatelne={editovatelne}
                        vybrane={vybrane}
                        onPrepnoutVyber={prepnoutVyber}
                        onPrepnoutVsechny={prepnoutVsechny}
                        onZrusitVyber={() => setVybrane(new Set())}
                        otevrenaId={uzky ? null : otevrenaId}
                        onOtevrit={(id) => setOtevrenaId(v => (v === id ? null : id))}
                        onHromadne={hromadneZaradit}
                        hromadneBezi={hromadneBezi}
                        renderEditor={(p) => editor(p)}
                    />

                    {/* Mobil a úzký sloupec: editace řádku je plátno zdola (norma
                        modalů 1d), ne šest třicetipixelových ovladačů v kartě. */}
                    {uzky && otevrenaPolozka && (
                        <ModalShell open onClose={() => setOtevrenaId(null)} isDark={isDark}
                            nadpis={otevrenaPolozka.nazev_z_dokladu} ikona={ListTree} barva={SECTION_COLOR} sirka={520}>
                            {editor(otevrenaPolozka)}
                        </ModalShell>
                    )}
                </>
            )}

            {novaModal && (
                <NovaSkladovaPolozkaModal
                    typ={novaModal.typ}
                    polozka={novaModal.polozka}
                    dodavatelId={doklad.dodavatel_id}
                    dokladDatum={doklad.datum}
                    jednotky={jednotky}
                    token={token}
                    cardStyle={cardStyle}
                    isDark={isDark}
                    onClose={() => setNovaModal(null)}
                    onHotovo={(created) => {
                        const t = novaModal.typ;
                        const fk = TYP_KATALOG_FK[t];
                        setKatalog?.(prev => ({
                            ...prev,
                            [t]: [...(prev[t] || []), { id: created.id, nazev: created.nazev }]
                                .sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs')),
                        }));
                        updatePolozka(novaModal.polozka, { [fk]: created.id, je_potvrzeno: true });
                        setNovaModal(null);
                    }}
                />
            )}
        </div>
    );
}

// ─── Editor jedné položky (rozbalený řádek / plátno na mobilu) ───────────────

function PolozkaRow({ polozka, kategorie, katalog = {}, jednotky = [], isDark, isAdmin, uzky = false, bezNazvu = false, onUpdate, onDelete, onAktualizovatCenu, onVytvoritJednotku, onVytvoritSkladovou, dokladZamceny = false }) {
    const ucty = leafKategorie(kategorie);

    // Zámky: měkký (potvrzená položka → jen read-only + Upravit) a tvrdý (schválený doklad → PIN)
    const potvrzeno = polozka.je_potvrzeno;
    const editable = isAdmin && !dokladZamceny && !potvrzeno;

    // Jednotky napojené skladové položky (základní + alt s koeficientem) + draft množství
    const fk = TYP_KATALOG_FK[polozka.typ_polozky];
    const skladId = fk ? polozka[fk] : null;
    const [itemJednotky, setItemJednotky] = useState([]);
    const [mnozstviDraft, setMnozstviDraft] = useState(null);
    useEffect(() => {
        if (!skladId || !polozka.typ_polozky) { setItemJednotky([]); return; }
        let alive = true;
        fetch(`/api/finance/doklady/skladova-jednotky?typ=${polozka.typ_polozky}&id=${skladId}`)
            .then(r => (r.ok ? r.json() : []))
            .then(d => { if (alive) setItemJednotky(Array.isArray(d) ? d : []); })
            .catch(() => {});
        return () => { alive = false; };
    }, [skladId, polozka.typ_polozky]);

    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const maKatalog = !!(polozka.typ_polozky && katalog[polozka.typ_polozky]);
    const opts = (maKatalog && katalog[polozka.typ_polozky]) || [];
    const nenapojeno = maKatalog && !polozka[fk];
    const napojeno = fk && polozka[fk];

    // Cena za jednotku z dokladu proti ceníku dodavatele k datu nákupu — tu
    // schválení přepíše. „Aktuální" cena může být z novějšího nákupu nebo od
    // jiného dodavatele, u starších dokladů by hlásila falešný rozdíl.
    const mnozstvi = Number(polozka.mnozstvi) || 0;
    const dokladCena = mnozstvi > 0
        ? (Number(polozka.cena_celkem_bez_dph) || 0) / mnozstvi
        : (Number(polozka.cena_za_jednotku) || 0);
    const vCeniku = polozka.cena_dodavatele_k_datu != null ? Number(polozka.cena_dodavatele_k_datu) : null;
    const aktualni = polozka.aktualni_cena_jednotka != null ? Number(polozka.aktualni_cena_jednotka) : null;
    const rozdil = vCeniku != null ? dokladCena - vCeniku : null;
    const rozdilny = rozdil != null && Math.abs(rozdil) > 0.009;

    const ulozMnozstvi = () => {
        if (mnozstviDraft == null) return;
        const num = Number(mnozstviDraft);
        if (!Number.isNaN(num) && num !== Number(polozka.mnozstvi)) onUpdate({ mnozstvi: num });
        setMnozstviDraft(null);
    };

    const jednotkaOpts = itemJednotky.length > 0
        ? [{ value: '', label: '— jednotka —' }, ...itemJednotky.map(j => ({ value: j.jednotka_id, label: `${j.zkratka} · ${j.nazev}${j.zakladni ? ' (zákl.)' : ''}` }))]
        : [{ value: '', label: '— jednotka —' }, ...jednotky.map(j => ({ value: j.id, label: `${j.zkratka} · ${j.nazev}` })), { value: '__new__', label: '+ Nová jednotka…' }];

    const zmenJednotku = async (v) => {
        if (v === '__new__') { const id = await onVytvoritJednotku?.(); if (id) onUpdate({ jednotka_id: id }); return; }
        const novaId = v ? Number(v) : null;
        const stara = itemJednotky.find(j => j.jednotka_id === polozka.jednotka_id);
        const nova = itemJednotky.find(j => j.jednotka_id === novaId);
        // Přepnutí jednotky přepočítá množství koeficientem, ať se nenaskladní
        // dvacet kusů místo dvaceti balení.
        if (stara && nova && nova.koeficient > 0 && novaId !== polozka.jednotka_id) {
            const nm = Number(((Number(polozka.mnozstvi) || 0) * stara.koeficient / nova.koeficient).toFixed(4));
            onUpdate({ jednotka_id: novaId, mnozstvi: nm });
        } else {
            onUpdate({ jednotka_id: novaId });
        }
    };

    /**
     * Editor je plocha ve standardu kokpitu: neutrální bílý/černý závoj
     * a neutrální hranice. Modré pozadí se v aplikaci nepoužívá — akcent
     * nese text a obtažení ovladače, ne blok.
     */
    return (
        <div className="rounded-lg text-sm"
            style={{
                padding: 10,
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${hranice}`,
            }}>

            {/* Název (jen když ho nenese řádek nad editorem nebo hlavička plátna) */}
            {!bezNazvu && (
                <div className="font-medium truncate mb-2" style={{ color: 'var(--text-primary)' }} title={polozka.nazev_z_dokladu}>
                    {polozka.nazev_z_dokladu}
                </div>
            )}

            {/* Tři výběry vedle sebe: P&L účet · typ · konkrétní skladová položka.
                Dřív byl P&L účet sám vpravo nahoře, typ a katalog v druhé mřížce
                pod ním a mezi tím řádek meta — tři rozhodnutí o téže položce na
                třech místech. Na úzkém panelu jdou pod sebe. */}
            <div className="grid gap-2" style={{ gridTemplateColumns: uzky ? '1fr' : '1.2fr 1fr 1.2fr' }}>
                <div>
                    <div className="text-[9.5px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>P&amp;L účet</div>
                    {editable ? (
                        <Dropdown
                            value={polozka.kategorie_id || ''}
                            onChange={v => onUpdate({ kategorie_id: v ? Number(v) : null })}
                            options={[{ value: '', label: '— P&L účet —' }, ...ucty.map(k => ({ value: k.id, label: k.label }))]}
                            placeholder="— P&L účet —" isDark={isDark}
                            accent={polozka.kategorie_id ? SECTION_COLOR : WARN} height={30}
                        />
                    ) : (
                        <RoPole isDark={isDark} label={ucty.find(k => k.id === polozka.kategorie_id)?.label || '— P&L účet —'} />
                    )}
                </div>

                <div>
                    <div className="text-[9.5px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Typ</div>
                    {editable ? (
                        <Dropdown
                            value={polozka.typ_polozky || ''}
                            onChange={v => onUpdate({ typ_polozky: v || null, je_potvrzeno: false })}
                            options={TYP_POLOZKY}
                            placeholder="— typ —" isDark={isDark}
                            accent={polozka.typ_polozky ? SECTION_COLOR : BAD} height={30}
                        />
                    ) : (
                        <RoPole isDark={isDark} label={TYP_POLOZKY.find(t => t.value === (polozka.typ_polozky || ''))?.label || '—'} />
                    )}
                </div>

                <div>
                    <div className="text-[9.5px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Skladová položka</div>
                    {!maKatalog ? (
                        <div className="flex items-center" style={{ height: 30, fontSize: 12, color: 'var(--text-tertiary)' }}>
                            {polozka.typ_polozky ? 'katalog se nevede' : 'nejdřív typ'}
                        </div>
                    ) : editable ? (
                        <div className="flex items-center gap-1">
                            <div className="flex-1 min-w-0">
                                <Dropdown
                                    value={polozka[fk] || ''}
                                    onChange={v => onUpdate({ [fk]: v ? Number(v) : null })}
                                    options={[{ value: '', label: '— vyber z katalogu —' }, ...opts.map(o => ({ value: o.id, label: o.nazev }))]}
                                    placeholder="— vyber z katalogu —" isDark={isDark}
                                    accent={nenapojeno ? BAD : SECTION_COLOR} height={30}
                                />
                            </div>
                            <button
                                onClick={() => onVytvoritSkladovou?.()}
                                title="Založit novou skladovou položku a napárovat"
                                className="shrink-0 inline-flex items-center justify-center rounded-md"
                                style={{
                                    width: 30, height: 30, color: 'var(--text-secondary)',
                                    border: `1px solid ${hranice}`, background: 'transparent',
                                }}
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                    ) : (
                        <RoPole isDark={isDark} label={opts.find(o => o.id === polozka[fk])?.nazev || '— nenapojeno —'} />
                    )}
                </div>
            </div>

            {/* Jeden řádek pod výběry: naskladnění (množství + jednotka) a cena.
                Ukazuje se až po napojení na katalog — bez něj se nic neskladní
                a cena nemá kam zapadnout. */}
            {napojeno ? (
                <div className="flex items-center gap-2 flex-wrap mt-2 pt-2" style={{ borderTop: `1px dashed ${hranice}` }}>
                    <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Naskladní</span>
                    {editable ? (
                        <>
                            <input type="number" step="0.001" value={mnozstviDraft ?? (polozka.mnozstvi ?? '')}
                                onChange={e => setMnozstviDraft(e.target.value)}
                                onBlur={ulozMnozstvi}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                className="px-2 rounded-md font-semibold tabular-nums"
                                style={{
                                    width: 72, height: 28, background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
                                    border: `1px solid ${hranice}`, color: 'var(--text-primary)', fontSize: 13, outline: 'none',
                                }} />
                            <div style={{ width: 132 }}>
                                <Dropdown value={polozka.jednotka_id || ''} onChange={zmenJednotku} options={jednotkaOpts}
                                    placeholder="— jednotka —" isDark={isDark} accent={SECTION_COLOR} height={28} />
                            </div>
                        </>
                    ) : (
                        <b className="tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmt(mnozstvi)} {polozka.jednotka?.zkratka || ''}</b>
                    )}

                    <span style={{ color: 'var(--text-tertiary)' }}>·</span>

                    <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Cena/j</span>
                    <b className="tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmt(dokladCena)} Kč</b>
                    {vCeniku != null && (
                        <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                            vs {fmt(vCeniku)} v ceníku
                        </span>
                    )}
                    {aktualni != null && (vCeniku == null || Math.abs(aktualni - vCeniku) > 0.009) && (
                        <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>· {fmt(aktualni)} nyní</span>
                    )}
                    {rozdilny && (
                        <span className="text-[12px] font-bold tabular-nums" style={{ color: rozdil > 0 ? BAD : OK }}>
                            {rozdil > 0 ? '▲' : '▼'} {fmt(Math.abs(rozdil))}
                        </span>
                    )}
                    {vCeniku != null && !rozdilny ? (
                        <span className="text-[11px]" style={{ color: OK }}>✓ ceník sedí</span>
                    ) : isAdmin && !dokladZamceny ? (
                        /* Schválení dokladu zapíše ceny samo — tlačítko je zkratka,
                           když chce mít člověk cenu v ceníku ještě před schválením. */
                        <button onClick={() => onAktualizovatCenu?.()}
                            className="px-2 py-0.5 rounded-md text-[11px] font-semibold"
                            style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}` }}
                            title="Zapíše cenu do ceníku hned. Jinak se zapíše sama při schválení dokladu.">
                            Zapsat do ceníku
                        </button>
                    ) : null}
                </div>
            ) : nenapojeno ? (
                <div className="text-[11px] mt-2 flex items-center gap-1" style={{ color: BAD }}>
                    <AlertCircle size={11} /> Bez vazby do katalogu se položka nenaskladní a nejde schválit.
                </div>
            ) : null}

            {/* Potvrdit / potvrzeno + mazání — jeden řádek na konci editoru */}
            <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: `1px dashed ${hranice}` }}>
                {editable && polozka.typ_polozky && polozka.kategorie_id && (
                    <button
                        onClick={() => onUpdate({ je_potvrzeno: true })}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold"
                        style={{ background: `${OK}1f`, color: OK, border: `1px solid ${OK}59` }}
                        title="Zamkne řádek a uloží mapping pro automatické typování příštích dokladů"
                    >
                        <Check size={11} /> Potvrdit a zamknout
                    </button>
                )}
                {potvrzeno && (
                    <>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: OK }}>
                            <Lock size={11} /> Potvrzeno
                        </span>
                        {isAdmin && !dokladZamceny && (
                            <button onClick={() => onUpdate({ je_potvrzeno: false })}
                                className="px-2 py-1 rounded-md text-[11px]"
                                style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}` }}>
                                Odemknout
                            </button>
                        )}
                    </>
                )}
                <span className="flex-1" />
                {isAdmin && !dokladZamceny && (
                    <button onClick={onDelete}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium"
                        style={{ color: BAD, border: `1px solid ${hranice}` }}
                        title="Smazat položku">
                        <Trash2 size={11} /> Smazat
                    </button>
                )}
            </div>
        </div>
    );
}
